/**
 * TabOutcomes — the "Outcomes" tab body on UniversityDetail.
 *
 * Extracted from UniversityDetail.jsx as part of the P2 refactor. Renders
 * the writeup, the EmployerFlow Sankey-lite for editorial-tier unis, and
 * the two-column outcomes/top-employers cards.
 */
import { Link } from "react-router-dom";
import { Database } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import TabWriteup from "@/components/app/university/TabWriteup";
import EmployerFlow from "@/components/app/EmployerFlow";
import { tagForCountry, labelForTag } from "@/lib/employerSources";

function DataRow({ k, v }) {
  return (
    <div className="data-row">
      <div className="data-key">{k}</div>
      <div className="data-val">{v}</div>
    </div>
  );
}

function employerSlug(e) {
  // Same kebab-case derivation the EmployerFlow widget uses — keeps the
  // deep-link identical no matter which surface the user came from.
  return String(e)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[.']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function TabOutcomes({ uni, format }) {
  // Infer the data-source tag from the uni's country. The uni-level
  // Top-employers card holds a plain array of strings (no per-chip
  // source), so we show one card-wide provenance pill explaining
  // where this country's employer-feeder data comes from. Same
  // labels as the per-firm pill on EmployerDetail.
  const sourceTag = tagForCountry(uni?.country);
  const sourceMeta = sourceTag ? labelForTag(sourceTag) : null;
  return (
    <>
      <TabWriteup uniId={uni.id} tab="outcomes" />
      {uni.source_tier === "editorial" && (
        <EmployerFlow uniId={uni.id} uniName={uni.name} />
      )}
      <div className="grid lg:grid-cols-2 gap-8">
        <Card className="p-6 border-stone-200">
          <div className="label-uppercase">Career outcomes</div>
          <div className="mt-4">
            <DataRow k="Employment (6 mo)" v={`${(uni.employment_rate * 100).toFixed(0)}%`} />
            <DataRow k="Avg. starting salary" v={uni.avg_starting_salary_usd != null ? format(uni.avg_starting_salary_usd) : "—"} />
            <DataRow k="Internships taken" v={`${(uni.internship_pct * 100).toFixed(0)}%`} />
            <DataRow k="Enter postgrad" v={`${(uni.postgrad_pct * 100).toFixed(0)}%`} />
            <DataRow k="Alumni strength" v={uni.alumni_strength} />
            <DataRow k="Employer reputation" v={`#${uni.employer_reputation_rank}`} />
          </div>
        </Card>
        <Card className="p-6 border-stone-200">
          <div className="label-uppercase">Top employers</div>
          {sourceMeta && (uni.top_employers || []).length > 0 && (
            <div
              data-testid="uni-top-employers-source"
              className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-stone-600"
            >
              <span className="inline-flex items-center gap-1 text-stone-500">
                <Database className="w-3 h-3" /> Data source:
              </span>
              <span
                data-testid={`uni-top-employers-source-${sourceTag}`}
                title={sourceMeta.note}
                className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-stone-700"
              >
                {sourceMeta.label}
              </span>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2" data-testid="uni-top-employer-chips">
            {(uni.top_employers || []).map((e) => {
              const slug = employerSlug(e);
              return (
                <Link
                  key={e}
                  to={`/how-to-get-into/${slug}`}
                  data-testid={`uni-top-employer-chip-${slug}`}
                  className="inline-block"
                >
                  <Badge
                    variant="secondary"
                    className="rounded-md font-mono hover:bg-[#EDE9FE] hover:text-[#4C1D95] transition-colors cursor-pointer"
                  >
                    {e}
                  </Badge>
                </Link>
              );
            })}
          </div>
          <div className="mt-6 label-uppercase">Notable alumni</div>
          <ul className="mt-3 text-sm space-y-1">
            {(uni.notable_alumni || []).map((a) => (
              <li key={a}>· {a}</li>
            ))}
          </ul>
        </Card>
      </div>
    </>
  );
}
